import { useCallback, useContext, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { TranscriptSocketEvent } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ExtractedAnswersList } from '@trinity/chat-components/components/ExtractedAnswers/index.js';
import { ExtractedAnswersAPIContext } from '@trinity/chat-components/contexts/ExtractedAnswersAPIContext.js';
import { RefreshButton } from '@trinity/chat-components/components/RefreshButton/index.js';
import { useExtractedAnswers } from '@trinity/chat-components/hooks/useExtractedAnswers';
import { useRefreshExtractedAnswers } from '@trinity/chat-components/hooks/useRefreshExtractedAnswers';
import { useTranscriptsSocket } from '@trinity/chat-components/websocket/hooks/useTranscriptsSocket.js';
import { TranscriptIdContext } from '@/containers/Transcript/context';
import { Skeleton } from '@/components/Transcript';

const Root = styled.div({
  border: '1px solid var(--sec-01)',
  borderRadius: 5,
  minHeight: 500,
  width: '100%',
  margin: '20px 0',
});

const Header = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 15px',
  height: 60,
  borderBottom: '1px solid var(--sec-01)',
});

const Title = styled.div({
  fontSize: 22,
  color: 'var(--black-l)',
  fontFamily: 'var(--font-semibold)',
});

const Main = styled.div({
  padding: 15,
  paddingTop: 5,
});

const ErrorMessage = styled.div({
  color: '#991b1b',
  padding: 16,
  textAlign: 'center',
});

const EmptyMessage = styled.div({
  color: 'var(--gray)',
  lineHeight: '30px',
  textAlign: 'center',
  padding: '20px 0',
});

function ExtractedAnswersContent(): React.ReactElement {
  const transcriptId = useContext(TranscriptIdContext);
  const queryClient = useQueryClient();
  const { refresh, isRefreshing } = useRefreshExtractedAnswers(transcriptId);

  const { data, isLoading, error } = useExtractedAnswers(transcriptId, {
    enabled: !!transcriptId,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Subscribe to transcript WebSocket for real-time updates
  const socketKey = useMemo(() => (transcriptId ? { transcriptId } : undefined), [transcriptId]);
  const socket = useTranscriptsSocket(socketKey);

  const handleExtractedAnswerAvailable = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['transcript:extracted-answers', { transcriptId }],
    });
  }, [queryClient, transcriptId]);

  useEffect(() => {
    if (!socket) return;

    socket.on(TranscriptSocketEvent.ExtractedAnswerAvailable, handleExtractedAnswerAvailable);
    return () => {
      socket.off(TranscriptSocketEvent.ExtractedAnswerAvailable, handleExtractedAnswerAvailable);
    };
  }, [socket, handleExtractedAnswerAvailable]);

  const items = data?.items ?? [];
  const hasData = !isLoading && items.length > 0;
  const hasNoData = !isLoading && !error && items.length === 0;

  return (
    <Root>
      <Header>
        <Title>Extracted Answers</Title>
        <RefreshButton refreshing={isRefreshing} onClick={refresh} />
      </Header>
      <Main>
        {isLoading && <Skeleton.Summary rows={3} />}
        {error && <ErrorMessage>Failed to load extracted answers.</ErrorMessage>}
        {hasData && <ExtractedAnswersList items={items} transcriptId={transcriptId} />}
        {hasNoData && <EmptyMessage>No extracted answers available for this transcript.</EmptyMessage>}
      </Main>
    </Root>
  );
}

export function ExtractedAnswers(): React.ReactElement {
  const apiClient = useChatApiClient();

  const contextValue = {
    getExtractedAnswers: apiClient.transcripts.getExtractedAnswers.bind(apiClient.transcripts),
    refreshExtractedAnswers: apiClient.transcripts.refreshExtractedAnswers.bind(apiClient.transcripts),
  };

  return (
    <ExtractedAnswersAPIContext.Provider value={contextValue}>
      <ExtractedAnswersContent />
    </ExtractedAnswersAPIContext.Provider>
  );
}

ExtractedAnswers.displayName = 'Tab.ExtractedAnswers';
